'use client'

import { FormattedMessage, useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import AppliedFilterSection from '../../shared/AppliedFilterSection'
import CategoricalFilter from '../../shared/CategoricalFilter'
import FilterCheckbox from '../../shared/FilterCheckbox'
import {
  useFilterOptions,
  createCategoricalAppliedComponent,
} from '../../shared/filterUtils'
import { checkboxRowPadding } from '../../shared/OnlyableCheckbox'
import toDisplayNames from '../../toDisplayNames'
import type { AppliedProps, FilterProps, ListingFilter } from '../../types'

const KEY = 'buyingFormat'
const CC_KEY = 'isCreditCardEnabled'
const toDisplayName = toDisplayNames[KEY]

// Reuse the categorical option row padding so the Credit Card toggle sits flush
// within the same <ol> as the buyingFormat options.
const CreditCardItem = styled.li`
  display: flex;
  align-items: center;
  padding: ${checkboxRowPadding};
`

const BuyingFormatFilter: ListingFilter<Set<string>> = ({
  onChange,
  desktopScreen,
  items: itemsData,
  value,
}: FilterProps<Set<string>>) => {
  const intl = useIntl()
  const items = useFilterOptions(itemsData, toDisplayName)
  const { state, updateFilters } = useListingSearch()
  const { track } = useAnalytics()

  const isCreditCardEnabled = state.filters[CC_KEY] === true

  const handleCreditCardToggle = () => {
    const next = !isCreditCardEnabled
    // Fire only on enable so the event matches its name ("applied"); disabling
    // is not an "apply". Event name is provisional until the ALP analytics event
    // schema is finalized.
    if (next) {
      track('cc_filter_applied', { value: next })
    }
    updateFilters({ [CC_KEY]: next })
  }

  return (
    <CategoricalFilter
      items={items}
      selected={value}
      name={intl.formatMessage({ id: `Filters.${KEY}` })}
      onChange={onChange}
      desktopScreen={desktopScreen}
      appendItems={
        <CreditCardItem>
          <FilterCheckbox
            checked={isCreditCardEnabled}
            label={<FormattedMessage id={`Filters.${CC_KEY}`} />}
            onChange={handleCreditCardToggle}
          />
        </CreditCardItem>
      }
    />
  )
}

BuyingFormatFilter.expandedOnInit = true
// Always render the section so the Credit Card checkbox is reachable even when
// the buyingFormat facet returns no options.
BuyingFormatFilter.isAlwaysDisplayed = true
BuyingFormatFilter.label = `Filters.${KEY}`

BuyingFormatFilter.getOwnProps = ({ state, data, updateFilters }) => ({
  filterKey: KEY,
  items: data?.allFilters[KEY] || [],
  value: new Set(state.filters[KEY]),
  onChange: (value: Set<string>) => updateFilters({ [KEY]: [...value] }),
})

const CategoricalApplied = createCategoricalAppliedComponent(
  KEY,
  `Filters.${KEY}`,
  toDisplayName
)

// Applied chips for this section cover both the buyingFormat selections and the
// (separate) isCreditCardEnabled boolean, since they live in one filter section.
BuyingFormatFilter.Applied = function BuyingFormatApplied(props: AppliedProps) {
  const { filtersState, updateFilters, noClose } = props

  return (
    <>
      <CategoricalApplied {...props} />
      {filtersState[CC_KEY] ? (
        <AppliedFilterSection
          label={`Filters.${CC_KEY}`}
          values={[
            {
              key: CC_KEY,
              name: <FormattedMessage id={`Filters.${CC_KEY}.applied`} />,
            },
          ]}
          onRemove={() => updateFilters({ [CC_KEY]: false })}
          noClose={noClose}
        />
      ) : null}
    </>
  )
}

export default BuyingFormatFilter
